<template>
  <section class="dashboard-panel" aria-labelledby="tasks-panel-title">
    <header class="dashboard-panel-header">
      <div>
        <h3 id="tasks-panel-title" class="dashboard-panel-title">{{ s__('Dashboard|Tasks') }}</h3>
        <p class="dashboard-panel-subtitle">
          {{ s__('Dashboard|Grouping') }}: {{ currentGroupingDisplay }}
        </p>
      </div>
      <div class="header-actions">
        <div class="dashboard-presentation-toggle" :aria-label="s__('Dashboard|Presentation')">
          <button
            v-for="option in presentationOptions"
            :key="option.value"
            type="button"
            class="dashboard-presentation-button"
            :class="{ active: dashboardPresentation === option.value }"
            :disabled="savingDashboardPresentation"
            @click="changePresentation(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
        <button
          type="button"
          class="btn-link dashboard-create-task-btn dashboard-header-create-btn"
          :aria-label="s__('Dashboard|Create Task')"
          @click="$emit('open-create-task')"
        >
          <span class="dashboard-create-task-label">{{ s__('Dashboard|Create Task') }}</span>
          <span class="dashboard-create-task-plus" aria-hidden="true">+</span>
        </button>
      </div>
    </header>

    <div class="dashboard-panel-body">
      <div class="dashboard-filter-group">
        <div class="dashboard-filter-options mobile-underlined-checklist">
          <label
            v-for="option in roleOptions"
            :key="option.value"
            class="dashboard-filter-option mobile-underlined-checklist-item"
          >
            <input
              type="checkbox"
              :checked="isRoleChecked(option.value)"
              :disabled="isRoleDisabled(option.value)"
              @change="toggleRole(option.value)"
            />
            <span>{{ option.label }}</span>
          </label>
        </div>
      </div>

      <div v-if="filterAllAccessible" class="dashboard-hint">
        {{ s__('Dashboard|All accessible tasks are shown.') }}
      </div>

      <div class="dashboard-advanced-panel">
        <button
          type="button"
          class="dashboard-advanced-toggle"
          @click="isAdvancedExpanded = !isAdvancedExpanded"
        >
          <span class="dashboard-advanced-title">{{ s__('Dashboard|Advanced settings') }}</span>
          <span class="dashboard-advanced-meta">
            <span v-if="advancedActiveCount > 0" class="dashboard-advanced-count">{{
              advancedActiveCount
            }}</span>
            <span class="dashboard-advanced-chevron">{{ isAdvancedExpanded ? '▼' : '▶' }}</span>
          </span>
        </button>
        <div v-if="isAdvancedExpanded" class="dashboard-advanced-content">
          <task-filter-panel
            :members="members"
            :priorities="priorities"
            :workflow-statuses="workflowStatuses"
            :grouped-workflow-statuses="groupedWorkflowStatuses"
            :labels="labels"
            :task-categories="taskCategories"
            :grouped-task-categories="groupedTaskCategories"
            :initial-filters="advancedFilters"
            :initially-expanded="true"
            :lock-expanded="true"
            :is-personal-project="false"
            :show-task-type-filter="true"
            :personal-labels-enabled="personalLabelsEnabled"
            :projects="projectsForFilter"
            @apply="$emit('apply-advanced-filters', $event)"
          />

          <div class="dashboard-advanced-controls-row">
            <SingleSelectSetting
              v-model="selectedGroupBy"
              :label="s__('Dashboard|Grouping')"
              :options="groupingOptions"
              input-id="dashboard-task-grouping-select"
            />

            <OrderingPanel
              :order-by="orderBy"
              :order-direction="orderDirection"
              :default-order-by="'due_at'"
              :default-order-direction="'asc'"
              :show-save-as-default="false"
              :reset-label="s__('Tasks|Reset Ordering')"
              @change="$emit('update-ordering', $event)"
              @reset="$emit('reset-ordering')"
            />
          </div>

          <div
            class="dashboard-inline-actions dashboard-inline-actions-task-settings mobile-stretch-btn-group"
          >
            <button
              type="button"
              class="btn-secondary btn-sm mobile-stretch-btn"
              :disabled="savingDefaultSettings"
              @click="$emit('save-as-default')"
            >
              {{
                savingDefaultSettings
                  ? s__('Dashboard|Saving...')
                  : s__('Dashboard|Save as default')
              }}
            </button>
            <button
              type="button"
              class="btn-secondary btn-sm mobile-stretch-btn"
              :disabled="savingDefaultSettings || !hasStoredDefaultSettings"
              @click="$emit('restore-saved-settings')"
            >
              {{ s__('Dashboard|Restore saved settings') }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="loading" class="dashboard-empty-message dashboard-inline-loading">
        {{ __('Loading…') }}
      </div>
      <div
        v-else-if="groupedTasks.length === 0"
        class="dashboard-empty-message dashboard-inline-loading"
      >
        {{ s__('Dashboard|No tasks') }}
      </div>
      <div v-else class="dashboard-task-groups">
        <section v-for="group in groupedTasks" :key="group.key" class="dashboard-task-group">
          <header class="dashboard-task-group-header">
            <button
              v-if="group.key !== 'none'"
              type="button"
              class="dashboard-task-group-toggle"
              :aria-expanded="String(!isGroupCollapsed(group.key))"
              @click="toggleGroup(group.key)"
            >
              <span class="dashboard-task-group-main">
                <strong>{{ group.title }}</strong>
                <span class="dashboard-muted">{{ group.tasks.length }}</span>
              </span>
              <span class="dashboard-task-group-chevron">{{
                isGroupCollapsed(group.key) ? '▶' : '▼'
              }}</span>
            </button>
            <div v-else class="dashboard-task-group-main">
              <strong>{{ group.title }}</strong>
              <span class="dashboard-muted">{{ group.tasks.length }}</span>
            </div>
          </header>
          <TaskPreviewTable
            v-if="isTablePresentation && !isGroupCollapsed(group.key)"
            :rows="taskRowsForTable(group.tasks)"
            :project-scoped="dashboardScope === 'project'"
            :empty-text="s__('Dashboard|No tasks')"
            :clickable-labels="true"
            :clickable-personal-labels="true"
            :chat-id="chatId"
            :from-id="fromId"
            @label-click="openProjectLabelTasksModal"
            @personal-label-click="openPersonalLabelTasksModal"
          />
          <ul v-else-if="!isGroupCollapsed(group.key)" class="dashboard-task-list">
            <task-row
              v-for="task in group.tasks"
              :key="task.id"
              :row-data-task-id="String(task.iid)"
              :task="task"
              :dashboard-scope="dashboardScope"
              :chat-id="chatId"
              :from-id="fromId"
              :timezone="userTimezone"
              :locale="userLocale"
              :checklists-enabled="false"
              @transition-executed="$emit('transition-executed', $event)"
            />
          </ul>
        </section>

        <div v-if="tasksHasNextPage || loadingMoreTasks" class="dashboard-inline-loading">
          <button
            type="button"
            class="btn-load-more"
            :disabled="loadingMoreTasks || !tasksHasNextPage"
            @click.prevent.stop="$emit('load-more')"
          >
            {{ loadingMoreTasks ? __('Loading…') : s__('Tasks|Show more') }}
          </button>
        </div>
      </div>
    </div>
    <LabelTasksModal
      :visible="!!selectedLabel"
      :label="selectedLabel"
      :scope="selectedLabelScope"
      :project-id="selectedLabel?.projectId || selectedLabel?.project_id"
      :chat-id="chatId"
      :from-id="fromId"
      @label-click="openScopedLabelTasksModal"
      @close="selectedLabel = null"
    />
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { __, s__ } from '@/locale'
import TaskFilterPanel from '../tasks/TaskFilterPanel.vue'
import OrderingPanel from '../common/OrderingPanel.vue'
import SingleSelectSetting from '../common/SingleSelectSetting.vue'
import LabelTasksModal from '../common/LabelTasksModal.vue'
import TaskPreviewTable from '../common/TaskPreviewTable.vue'
import TaskRow from '../tasks/TaskRow.vue'
import '../../styles/mobile-underlined-checklist.css'

const props = defineProps({
  loading: { type: Boolean, default: false },
  tasks: { type: Array, default: () => [] },
  loadingMoreTasks: { type: Boolean, default: false },
  tasksHasNextPage: { type: Boolean, default: false },
  filterAsAuthor: { type: Boolean, default: true },
  filterAsAssignee: { type: Boolean, default: true },
  filterAsSubscriber: { type: Boolean, default: true },
  filterAllAccessible: { type: Boolean, default: false },
  advancedFilters: { type: Object, required: true },
  advancedActiveCount: { type: Number, default: 0 },
  groupBy: { type: String, default: 'by_due_date' },
  orderBy: { type: String, default: 'due_at' },
  orderDirection: { type: String, default: 'asc' },
  members: { type: Array, default: () => [] },
  priorities: { type: Array, default: () => [] },
  workflowStatuses: { type: Array, default: () => [] },
  groupedWorkflowStatuses: { type: Object, default: () => ({}) },
  labels: { type: Array, default: () => [] },
  taskCategories: { type: Array, default: () => [] },
  groupedTaskCategories: { type: Object, default: () => ({}) },
  projectsForFilter: { type: Array, default: () => [] },
  personalLabelsEnabled: { type: Boolean, default: false },
  chatId: { type: String, default: null },
  fromId: { type: String, default: null },
  userTimezone: { type: String, default: null },
  userLocale: { type: String, default: null },
  hasStoredDefaultSettings: { type: Boolean, default: false },
  savingDefaultSettings: { type: Boolean, default: false },
  dashboardPresentation: {
    type: String,
    default: 'list',
    validator: value => ['list', 'table'].includes(value),
  },
  savingDashboardPresentation: { type: Boolean, default: false },
  dashboardScope: {
    type: String,
    default: 'personal',
    validator: value => ['personal', 'project'].includes(value),
  },
})

const emit = defineEmits([
  'load-more',
  'update-role-filters',
  'apply-advanced-filters',
  'update-group-by',
  'update-ordering',
  'reset-ordering',
  'transition-executed',
  'open-create-task',
  'save-as-default',
  'restore-saved-settings',
  'update-presentation',
])

const isAdvancedExpanded = ref(false)
const collapsedGroupKeys = ref({})
const selectedLabel = ref(null)
const selectedLabelScope = ref('project')
const lastManualRoles = ref({
  filterAsAuthor: props.filterAsAuthor,
  filterAsAssignee: props.filterAsAssignee,
  filterAsSubscriber: props.filterAsSubscriber,
})

const roleOptions = computed(() => [
  { value: 'mine', label: s__('Dashboard|Mine') },
  { value: 'assigned', label: s__('Dashboard|Assigned') },
  { value: 'watching', label: s__('Dashboard|Watching') },
  { value: 'all', label: s__('Dashboard|All accessible') },
])

const presentationOptions = computed(() => [
  { value: 'list', label: s__('Dashboard|List') },
  { value: 'table', label: s__('Dashboard|Table') },
])

const isTablePresentation = computed(() => props.dashboardPresentation === 'table')

const selectedRolesCount = computed(() => {
  let count = 0
  if (props.filterAsAuthor) count += 1
  if (props.filterAsAssignee) count += 1
  if (props.filterAsSubscriber) count += 1
  return count
})

const selectedGroupBy = computed({
  get: () => props.groupBy || 'by_due_date',
  set: value => emit('update-group-by', value || 'by_due_date'),
})

const currentGroupingDisplay = computed(() => {
  const labels = {
    by_due_date: s__('Boards|By Due Date'),
    by_project: s__('Boards|By Project'),
    by_assignee: s__('Boards|By Assignee'),
    by_author: s__('Boards|By Author'),
    by_category: s__('Boards|By Category'),
    by_status: s__('Boards|By Status'),
    by_priority: s__('Boards|By Priority'),
    none: s__('TaskFilter|None'),
  }
  return labels[selectedGroupBy.value] || labels.by_due_date
})

const groupingOptions = computed(() => [
  { value: 'by_due_date', label: s__('Boards|By Due Date') },
  { value: 'by_project', label: s__('Boards|By Project') },
  { value: 'by_assignee', label: s__('Boards|By Assignee') },
  { value: 'by_author', label: s__('Boards|By Author') },
  { value: 'by_category', label: s__('Boards|By Category') },
  { value: 'by_status', label: s__('Boards|By Status') },
  { value: 'by_priority', label: s__('Boards|By Priority') },
  { value: 'none', label: s__('TaskFilter|None') },
])

const groupedTasks = computed(() => {
  const buckets = {}
  const terminalFinished = []
  const terminalArchived = []
  const mode = props.groupBy || 'by_due_date'

  if (mode === 'none') {
    return [
      {
        key: 'none',
        title: s__('Dashboard|All tasks'),
        tasks: [...props.tasks].sort(compareTasks),
      },
    ]
  }

  props.tasks.forEach(task => {
    if (mode === 'by_due_date') {
      if (task.archived) {
        terminalArchived.push(task)
        return
      }
      if (task.finishedAt) {
        terminalFinished.push(task)
        return
      }
    }

    const { key, title } = getGroupKeyAndTitle(task, mode)
    if (!buckets[key]) buckets[key] = { key, title, tasks: [] }
    buckets[key].tasks.push(task)
  })

  const groups = Object.values(buckets).map(group => ({
    ...group,
    tasks: [...group.tasks].sort(compareTasks),
  }))

  if (mode === 'by_due_date') {
    const ordered = ['overdue', 'today', 'upcoming', 'no_due']
    groups.sort((a, b) => ordered.indexOf(a.key) - ordered.indexOf(b.key))
    if (terminalFinished.length > 0) {
      groups.push({
        key: 'finished',
        title: s__('Dashboard|Finished'),
        tasks: [...terminalFinished].sort(compareTasks),
      })
    }
    if (terminalArchived.length > 0) {
      groups.push({
        key: 'archived',
        title: s__('TaskFilter|Include archived').replace('Include ', ''),
        tasks: [...terminalArchived].sort(compareTasks),
      })
    }
    return groups
  }

  groups.sort((a, b) => a.title.localeCompare(b.title))
  return groups
})

watch(
  groupedTasks,
  groups => {
    const next = {}
    groups.forEach(group => {
      next[group.key] = collapsedGroupKeys.value[group.key] === true
    })
    collapsedGroupKeys.value = next
  },
  { immediate: true }
)

function isGroupCollapsed(groupKey) {
  if (groupKey === 'none') return false
  return collapsedGroupKeys.value[groupKey] === true
}

function toggleGroup(groupKey) {
  collapsedGroupKeys.value = {
    ...collapsedGroupKeys.value,
    [groupKey]: !isGroupCollapsed(groupKey),
  }
}

function changePresentation(value) {
  if (value === props.dashboardPresentation) return
  emit('update-presentation', value)
}

function taskRowsForTable(tasks) {
  return tasks.map((task, index) => ({
    id: task.id,
    task_id: task.id,
    task_iid: task.iid,
    title: task.title,
    priority: task.priority,
    due_date: task.dueAt,
    finished_at: task.finishedAt,
    project_id: task.project?.id,
    project_title: task.project?.title || '',
    workflow_status_name: task.workflowStatus?.name || '',
    workflow_status_color: task.workflowStatus?.color || '',
    assignee_name: person(task.assignee),
    author_name: person(task.author),
    project_labels: (task.labels || []).map(label => ({
      ...label,
      projectId: task.project?.id,
    })),
    personal_labels: task.personalLabels || [],
    __key: `dashboard-task-${task.id || task.iid || index}`,
  }))
}

function person(user) {
  if (!user) return ''
  if (user.username) return `@${user.username}`
  return user.name || ''
}

function openProjectLabelTasksModal(label) {
  selectedLabelScope.value = 'project'
  selectedLabel.value = label
}

function openPersonalLabelTasksModal(label) {
  selectedLabelScope.value = 'personal'
  selectedLabel.value = label
}

function openScopedLabelTasksModal(payload) {
  if (!payload?.label) return
  selectedLabelScope.value = payload.scope || 'project'
  selectedLabel.value = payload.label
}

function normalizeComparable(value) {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'string') {
    const asDate = Date.parse(value)
    if (!Number.isNaN(asDate)) return asDate
    return value.toLowerCase()
  }
  return value
}

function compareTasks(a, b) {
  const orderBy = props.orderBy || 'due_at'
  const direction = props.orderDirection === 'desc' ? -1 : 1

  const getValue = task => {
    if (orderBy === 'title') return task.title || ''
    if (orderBy === 'priority') return getPriorityRank(task.priority)
    if (orderBy === 'updated_at') return task.updatedAt || task.createdAt || ''
    if (orderBy === 'created_at') return task.createdAt || ''
    if (orderBy === 'due_at') return task.dueAt || ''
    return task.dueAt || ''
  }

  const left = normalizeComparable(getValue(a))
  const right = normalizeComparable(getValue(b))

  if (left === null && right !== null) return 1
  if (left !== null && right === null) return -1
  if (left !== null && right !== null) {
    if (left < right) return -1 * direction
    if (left > right) return 1 * direction
  }

  const leftCreated = Date.parse(a.createdAt || '') || 0
  const rightCreated = Date.parse(b.createdAt || '') || 0
  if (leftCreated > rightCreated) return -1
  if (leftCreated < rightCreated) return 1
  return String(a.id).localeCompare(String(b.id))
}

function getPriorityRank(priority) {
  if (priority === null || priority === undefined || priority === '') return null
  const normalized = String(priority).toLowerCase()
  const ranks = {
    lowest: 0,
    low: 1,
    medium: 2,
    high: 3,
    highest: 4,
  }
  return ranks[normalized] ?? null
}

function getGroupKeyAndTitle(task, mode) {
  if (mode === 'by_due_date') {
    if (!task.dueAt) return { key: 'no_due', title: s__('Dashboard|No due date') }
    const now = new Date()
    const dueDate = new Date(task.dueAt)
    if (Number.isNaN(dueDate.getTime()))
      return { key: 'no_due', title: s__('Dashboard|No due date') }

    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const tomorrowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)

    if (dueDate < now) return { key: 'overdue', title: s__('Dashboard|Overdue') }
    if (dueDate >= todayStart && dueDate < tomorrowStart) {
      return { key: 'today', title: s__('Dashboard|Due today') }
    }
    return { key: 'upcoming', title: s__('Dashboard|Upcoming') }
  }

  if (mode === 'by_project') {
    const title = task.project?.title || s__('Boards|No Project')
    return { key: `project_${task.project?.id || 'none'}`, title }
  }
  if (mode === 'by_assignee') {
    const title = task.assignee?.username || task.assignee?.name || s__('TaskFilter|Unassigned')
    return { key: `assignee_${task.assignee?.id || 'none'}`, title }
  }
  if (mode === 'by_author') {
    const title = task.author?.username || task.author?.name || s__('Boards|Unknown')
    return { key: `author_${task.author?.id || 'none'}`, title }
  }
  if (mode === 'by_status') {
    const title = task.workflowStatus?.name || s__('Boards|No Status')
    return { key: `status_${task.workflowStatus?.id || 'none'}`, title }
  }
  if (mode === 'by_category') {
    const title = task.taskCategory?.name || s__('Boards|No Category')
    return { key: `category_${task.taskCategory?.id || 'none'}`, title }
  }
  if (mode === 'by_priority') {
    const title = task.priority || s__('TaskFilter|None')
    return { key: `priority_${task.priority || 'none'}`, title }
  }

  return { key: 'default', title: s__('Dashboard|Tasks') }
}

function isRoleChecked(roleName) {
  if (roleName === 'all') return props.filterAllAccessible
  if (roleName === 'mine') return props.filterAsAuthor
  if (roleName === 'assigned') return props.filterAsAssignee
  if (roleName === 'watching') return props.filterAsSubscriber
  return false
}

function isRoleDisabled(roleName) {
  if (roleName === 'all') return false
  if (props.filterAllAccessible) return true

  const isChecked = isRoleChecked(roleName)
  return isChecked && selectedRolesCount.value === 1
}

function toggleRole(roleName) {
  if (roleName === 'all') {
    if (!props.filterAllAccessible) {
      lastManualRoles.value = {
        filterAsAuthor: props.filterAsAuthor,
        filterAsAssignee: props.filterAsAssignee,
        filterAsSubscriber: props.filterAsSubscriber,
      }
      emit('update-role-filters', {
        filterAsAuthor: true,
        filterAsAssignee: true,
        filterAsSubscriber: true,
        filterAllAccessible: true,
      })
      return
    }

    const restore = {
      filterAsAuthor: lastManualRoles.value.filterAsAuthor,
      filterAsAssignee: lastManualRoles.value.filterAsAssignee,
      filterAsSubscriber: lastManualRoles.value.filterAsSubscriber,
    }
    if (!restore.filterAsAuthor && !restore.filterAsAssignee && !restore.filterAsSubscriber) {
      restore.filterAsAuthor = true
    }
    emit('update-role-filters', { ...restore, filterAllAccessible: false })
    return
  }

  if (props.filterAllAccessible) return

  const next = {
    filterAsAuthor: props.filterAsAuthor,
    filterAsAssignee: props.filterAsAssignee,
    filterAsSubscriber: props.filterAsSubscriber,
  }

  if (roleName === 'mine') next.filterAsAuthor = !next.filterAsAuthor
  if (roleName === 'assigned') next.filterAsAssignee = !next.filterAsAssignee
  if (roleName === 'watching') next.filterAsSubscriber = !next.filterAsSubscriber

  if (!next.filterAsAuthor && !next.filterAsAssignee && !next.filterAsSubscriber) {
    next.filterAsAuthor = true
  }
  lastManualRoles.value = next
  emit('update-role-filters', { ...next, filterAllAccessible: false })
}
</script>
